import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { mediatorAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { tableStyles, actionBtn } from '../../styles/orderTableStyles';
import { formatDateTime } from '../../utils/dateTime';
import PaginationBar from '../../components/common/PaginationBar';

const formatMoney = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const MediatorPayouts = () => {
  const { user, mediatorId } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pagination, setPagination] = useState({ totalPages: 1, totalElements: 0 });

  const resolveMediatorId = useCallback(
    () => mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id,
    [mediatorId]
  );

  const fetchData = useCallback(async (pageNum = 0) => {
    const medId = resolveMediatorId();
    if (!medId) return;

    setLoading(true);
    try {
      const [walletRes, settingsRes, txRes] = await Promise.all([
        mediatorAPI.getRewardWallet(),
        mediatorAPI.getRewardSettings(),
        mediatorAPI.getRewardTransactions({ page: pageNum, size: 15 }),
      ]);

      setWallet(walletRes.data?.data || null);
      setSettings(settingsRes.data?.data || null);

      const txData = txRes.data?.data || {};
      setTransactions(txData.data || []);
      setPagination({
        totalPages: txData.totalPages || 1,
        totalElements: txData.totalElements || 0,
      });
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
      toast.error(error.response?.data?.message || 'Failed to load reward wallet');
    } finally {
      setLoading(false);
    }
  }, [resolveMediatorId]);

  useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  const statCards = [
    {
      label: 'Wallet Balance',
      value: loading ? '...' : `₹${formatMoney(wallet?.balance)}`,
      color: '#0f766e',
      bg: '#ccfbf1',
      icon: 'paid',
    },
    {
      label: 'Total Earned',
      value: loading ? '...' : `₹${formatMoney(wallet?.totalEarned)}`,
      color: '#059669',
      bg: '#d1fae5',
      icon: 'trending',
    },
    {
      label: 'Total Paid Out',
      value: loading ? '...' : `₹${formatMoney(wallet?.totalPaidOut)}`,
      color: '#475569',
      bg: '#f1f5f9',
      icon: 'sub',
    },
    {
      label: 'Orders this month',
      value: loading ? '...' : (wallet?.totalOrders ?? 0),
      color: '#3b82f6',
      bg: '#dbeafe',
      icon: 'package',
    },
  ];

  const programEnabled = settings?.enabled === true;

  return (
    <div style={{ padding: '28px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '36px', fontWeight: 800, color: '#1e293b', margin: '0 0 8px 0' }}>
              Rewards & Payouts
            </h1>
            <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>
              {programEnabled
                ? `Earn ₹${formatMoney(settings?.rewardAmount)} for every ${settings?.ordersPerReward} orders each month — welcome, ${user?.username || 'Mediator'}.`
                : `Track your reward wallet, ${user?.username || 'Mediator'}. The reward program is currently paused by admin.`}
            </p>
          </div>
          <Button variant="secondary" onClick={() => fetchData(page)} disabled={loading} style={{ fontSize: '15px', padding: '12px 18px' }}>
            <Icon name="refresh-cw" size={16} /> Refresh
          </Button>
        </div>

        {programEnabled && (
          <div style={{
            padding: '18px 22px',
            borderRadius: '16px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            marginBottom: '24px',
          }}>
            <Icon name="paid" size={28} color="#059669" />
            <div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: '#065f46', marginBottom: '4px' }}>
                Reward rule: every {settings.ordersPerReward} orders → ₹{formatMoney(settings.rewardAmount)}
              </div>
              <div style={{ fontSize: '14px', color: '#047857', lineHeight: 1.6 }}>
                Credits are added automatically at {settings.ordersPerReward}, {settings.ordersPerReward * 2}, {settings.ordersPerReward * 3}… orders
                <strong> within the current calendar month</strong> (IST). Count resets to 0 at midnight on the 1st of each month.
                Wallet balance does not reset — admin pays it out when ready.
              </div>
            </div>
          </div>
        )}

        {!programEnabled && settings && (
          <div style={{
            padding: '18px 22px',
            borderRadius: '16px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '24px',
          }}>
            <Icon name="alert-triangle" size={28} color="#d97706" />
            <div style={{ fontSize: '15px', color: '#92400e', lineHeight: 1.5 }}>
              New reward credits are paused. Your existing wallet balance and history are still shown below.
            </div>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '20px',
          marginBottom: '28px',
        }}>
          {statCards.map((card) => (
            <div
              key={card.label}
              style={{
                background: '#fff',
                borderRadius: '18px',
                padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#64748b' }}>{card.label}</span>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={card.icon} size={18} color={card.color} />
                </div>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: card.color }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div style={tableStyles.wrapper}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1e293b' }}>Transaction History</h2>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              {pagination.totalElements} transaction{pagination.totalElements === 1 ? '' : 's'}
            </span>
          </div>

          <div style={tableStyles.scroll}>
            <table style={tableStyles.table}>
              <thead style={tableStyles.thead}>
                <tr>
                  {['Date', 'Type', 'Amount', 'Balance After', 'Description'].map((h) => (
                    <th key={h} style={tableStyles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                      Loading transactions...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '64px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Icon name="paid" size={44} color="#e2e8f0" />
                        <p style={{ color: '#64748b', fontSize: '16px', fontWeight: 600, margin: 0 }}>No reward transactions yet</p>
                        <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                          {programEnabled
                            ? 'Complete more orders to earn milestone rewards.'
                            : 'Transactions will appear here when rewards are credited or paid out.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tableStyles.td}>{formatDateTime(tx.createdAt)}</td>
                      <td style={tableStyles.td}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 700,
                          background: tx.type === 'CREDIT' ? '#d1fae5' : '#fee2e2',
                          color: tx.type === 'CREDIT' ? '#059669' : '#dc2626',
                        }}>
                          {tx.type === 'CREDIT' ? 'Reward Credit' : 'Payout'}
                        </span>
                      </td>
                      <td style={{ ...tableStyles.td, fontWeight: 700, color: tx.type === 'CREDIT' ? '#059669' : '#dc2626' }}>
                        {tx.type === 'CREDIT' ? '+' : '−'}₹{formatMoney(tx.amount)}
                      </td>
                      <td style={tableStyles.td}>₹{formatMoney(tx.balanceAfter)}</td>
                      <td style={{ ...tableStyles.td, color: '#475569', maxWidth: '420px' }}>{tx.description}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {(pagination.totalPages > 1 || pagination.totalElements > 0) && (
            <PaginationBar
              page={page}
              totalPages={pagination.totalPages}
              totalElements={pagination.totalElements}
              pageSize={10}
              onPageChange={setPage}
              style={{ marginTop: '12px' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MediatorPayouts;
